import { c as _c } from "react/compiler-runtime";
import { getDocumentUploadVaultKeys, isVaultDataDecrypted } from '@onefootprint/core';
import type { Entity, EntityOnboarding } from '@onefootprint/request-types/dashboard';
import { LinkButton, LoadingSpinner, Tooltip } from '@onefootprint/ui';
import { useTranslation } from 'react-i18next';
import useEntityVault from '@/entities/hooks/use-entity-vault';
import useAutoDecryptControls from '@/entity/hooks/use-auto-decrypt-controls';
import useDecryptControls from '@/entity/hooks/use-decrypt-controls';
import useOnboardingDocuments from '../../hooks/use-onboarding-documents';
type DecryptProps = {
  entity: Entity;
  onboarding: EntityOnboarding;
};

/** "Decrypt all" for the documents uploaded during an onboarding; renders nothing once they are decrypted. */
const Decrypt = t0 => {
  const $ = _c(27);
  const {
    entity,
    onboarding
  } = t0;
  const {
    t
  } = useTranslation("entity-details");
  const {
    documents
  } = useOnboardingDocuments(entity, onboarding);
  let t1;
  if ($[0] !== entity.id) {
    t1 = {
      entityId: entity.id
    };
    $[0] = entity.id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    data: vaultData,
    update: onDecryptSuccess
  } = useEntityVault(t1);
  const decryptControls = useDecryptControls();
  const autoDecryptControls = useAutoDecryptControls(entity.kind);
  const isDecrypting = decryptControls.isDecrypting || autoDecryptControls.isEntityAutoDecrypting(entity.id);
  if (!vaultData || !documents?.length) {
    return null;
  }
  const isAllDecrypted = documents.every(document => document.uploads.every(upload => getDocumentUploadVaultKeys(document, upload).some(di => isVaultDataDecrypted(vaultData.vault[di]))));
  if (isAllDecrypted) {
    return null;
  }
  if (isDecrypting) {
    let t2;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <LoadingSpinner size="compact" />;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    return t2;
  }
  let t2;
  if ($[3] !== vaultData.vault) {
    t2 = Object.keys(vaultData.vault);
    $[3] = vaultData.vault;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const isVaultEmpty = t2.length === 0;
  let t3;
  if ($[5] !== documents) {
    t3 = documents.every(_temp);
    $[5] = documents;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const canDecrypt = t3;
  let t4;
  if ($[7] !== decryptControls || $[8] !== documents || $[9] !== entity || $[10] !== onDecryptSuccess || $[11] !== onboarding.id || $[12] !== vaultData) {
    t4 = async () => {
      const documentDIs = decryptControls.getDisFromDocuments(documents, vaultData);
      const response = await decryptControls.decrypt({
        entity,
        fields: documentDIs,
        reason: `Examining documents for onboarding ${onboarding.id}`
      });
      onDecryptSuccess({
        vault: response,
        transforms: {}
      });
    };
    $[7] = decryptControls;
    $[8] = documents;
    $[9] = entity;
    $[10] = onDecryptSuccess;
    $[11] = onboarding.id;
    $[12] = vaultData;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const handleDecryptSubmit = t4;
  let t5;
  if ($[14] !== isVaultEmpty || $[15] !== t) {
    t5 = isVaultEmpty ? t(_temp2) : t(_temp3);
    $[14] = isVaultEmpty;
    $[15] = t;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const t6 = !canDecrypt;
  let t7;
  if ($[17] !== t) {
    t7 = t(_temp4);
    $[17] = t;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== handleDecryptSubmit || $[20] !== t6 || $[21] !== t7) {
    t8 = <LinkButton disabled={t6} onClick={handleDecryptSubmit}>{t7}</LinkButton>;
    $[19] = handleDecryptSubmit;
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== canDecrypt || $[24] !== t5 || $[25] !== t8) {
    t9 = <Tooltip disabled={canDecrypt} content={t5}>{t8}</Tooltip>;
    $[23] = canDecrypt;
    $[24] = t5;
    $[25] = t8;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  return t9;
};
export default Decrypt;
function _temp(d) {
  return d.canDecrypt;
}
function _temp2($) {
  return $.decrypt.vaultEmptyNotAllowed;
}
function _temp3($_0) {
  return $_0.decrypt.notAllowed;
}
function _temp4($_1) {
  return $_1.onboardings.userData.decrypt.decryptAll;
}